import React from 'react';
import styles from "./styles.module.scss";
import { CloseCircleGreyIcon, AddIconCircle, CaretDownIcon } from 'assets';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Input, RadioInput, CustomSelect, Textarea } from 'components/form';
import { Button } from 'components/button';

// Define types for each section's data
type BasicExpectationFormData = {
  employmentType: string;
  experienceLevel: string;
  yearsOfExperience: string;
  workEnvironment: string[];
  minSalary: string;
  maxSalary: string;
};

type ResponsibilityFormData = {
  title: string;
  description: string;
  checked: boolean;
}[];

type TechnicalSkillFormData = {
  title: string;
  expectation: string;
}[];

type SoftSkillFormData = {
  title: string;
  description: string;
  checked: boolean;
  rating: number;
}[];

type WorkCultureTraitFormData = {
  title: string;
  description: string;
  checked: boolean;
}[];

type WorkStyleFormData = {
  description: string;
  rating: number;
}[];

type PersonalityAlignmentFormData = {
  category: string;
  description: string;
  style: string;
  implication: string;
}[];

const basicExpectationSchema = yup.object().shape({
  employmentType: yup.string().required('Employment type is required'),
  experienceLevel: yup.string().required('Experience level is required'),
  yearsOfExperience: yup.string().required('Years of experience is required'),
  workEnvironment: yup.array().min(1, 'Select at least one work environment'),
  minSalary: yup.string().required('Minimum salary is required'),
  maxSalary: yup.string()
    .required('Maximum salary is required')
    .test('is-greater', 'Maximum salary must be greater than minimum salary',
      function (value) {
        const { minSalary } = this.parent;
        return Number(value) > Number(minSalary);
      })
});

const responsibilitySchema = yup.array().of(
  yup.object().shape({
    title: yup.string().required('Title is required'),
    description: yup.string().required('Description is required'),
  })
);

const technicalSkillSchema = yup.array().of(
  yup.object().shape({
    title: yup.string().required('Skill name is required'),
    expectation: yup.string().required('Expectation is required'),
  })
);

const softSkillSchema = yup.array().of(
  yup.object().shape({
    title: yup.string().required('Skill name is required'),
    description: yup.string().required('Description is required'),
    rating: yup.number().min(1).max(5).required('Rating is required'),
  })
);

const workCultureTraitSchema = yup.array().of(
  yup.object().shape({
    title: yup.string().required('Trait name is required'),
    description: yup.string().required('Description is required'),
  })
);

const workStyleSchema = yup.array().of(
  yup.object().shape({
    description: yup.string().required('Description is required'),
    rating: yup.number().min(1).max(7).required('Rating is required'),
  })
);

const personalityAlignmentSchema = yup.array().of(
  yup.object().shape({
    category: yup.string().required('Category is required'),
    description: yup.string().required('Description is required'),
    style: yup.string().required('Style is required'),
    implication: yup.string().required('Implication is required'),
  })
);

interface EditModalProps {
  isOpen: boolean;
  onClose: () => void;
  section: string;
  sectionTitle: string;
  currentData: any;
  onSave: (data: any) => void;
}

const EditModal: React.FC<EditModalProps> = ({
  isOpen,
  onClose,
  section,
  sectionTitle,
  currentData,
  onSave
}) => {
  const getDefaultValues = () => {
    switch (section) {
      case 'basicExpectation':
        return currentData || {
          employmentType: '',
          experienceLevel: '',
          yearsOfExperience: '',
          workEnvironment: [],
          minSalary: '',
          maxSalary: '',
        };
      case 'coreResponsibilities':
        return currentData || [];
      case 'technicalSkills':
        return currentData || [];
      case 'softSkills':
        return currentData || [];
      case 'workCultureTraits':
        return currentData || [];
      case 'workStyles':
        return currentData || [];
      case 'personalityAlignments':
        return currentData || [];
      default:
        return {};
    }
  };

  const getSchema = () => {
    switch (section) {
      case 'basicExpectation':
        return basicExpectationSchema;
      case 'coreResponsibilities':
        return responsibilitySchema;
      case 'technicalSkills':
        return technicalSkillSchema;
      case 'softSkills':
        return softSkillSchema;
      case 'workCultureTraits':
        return workCultureTraitSchema;
      case 'workStyles':
        return workStyleSchema;
      case 'personalityAlignments':
        return personalityAlignmentSchema;
      default:
        return yup.object();
    }
  };

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    watch,
    setValue,
    reset
  } = useForm({
    defaultValues: getDefaultValues(),
    resolver: yupResolver(getSchema() as any)
  });

  React.useEffect(() => {
    reset(getDefaultValues());
  }, [currentData, section]);

  const onSubmit = (data: any) => {
    onSave(data);
    onClose();
  };

  const renderBasicExpectationForm = () => {
    const employmentTypeOptions = [
      { value: 'fulltime', label: 'Full-time' },
      { value: 'parttime', label: 'Part-time' },
      { value: 'contract', label: 'Contract' },
      { value: 'internship', label: 'Internship' }
    ];

    const experienceLevelOptions = [
      { value: 'entry', label: 'Entry Level' },
      { value: 'associate', label: 'Associate' },
      { value: 'mid', label: 'Mid Level' },
      { value: 'senior', label: 'Senior/Executive' }
    ];

    const yearsOfExperienceOptions = [
      { value: '0-2', label: '0-2 years' },
      { value: '1-3', label: '1-3 years' },
      { value: '3-5', label: '3-5 years' },
      { value: '5-8', label: '5-8 years' },
      { value: '8-10', label: '8-10 years' },
      { value: '10+', label: '10+ years' }
    ];

    const workEnvironmentOptions = [
      { value: 'Startup', label: 'Startup' },
      { value: 'Growing Business', label: 'Growing Business' },
      { value: 'Established organization', label: 'Established Organization' },
      { value: 'Large Corporate', label: 'Large Corporate' },
      { value: 'Freelance', label: 'Freelance' }
    ];

    return (
      <div className={styles.formSection}>
        <RadioInput
          label="Employment Type"
          register={register}
          name="employmentType"
          validatorMessage={errors.employmentType?.message as string}
          options={employmentTypeOptions}
          type="radio"
          value={watch('employmentType') as string}
          parentClassName={styles.optionsContainer}
        />

        <RadioInput
          label="Experience Level"
          register={register}
          name="experienceLevel"
          validatorMessage={errors.experienceLevel?.message as string}
          options={experienceLevelOptions}
          type="radio"
          value={watch('experienceLevel') as string}
          parentClassName={styles.optionsContainer}
        />

        <RadioInput
          label="Years of Experience"
          register={register}
          name="yearsOfExperience"
          validatorMessage={errors.yearsOfExperience?.message as string}
          options={yearsOfExperienceOptions}
          type="radio"
          value={watch('yearsOfExperience') as string}
          parentClassName={styles.optionsContainer}
        />

        <RadioInput
          label="Work Environment Preference"
          register={register}
          name="workEnvironment"
          validatorMessage={errors.workEnvironment?.message as string}
          options={workEnvironmentOptions}
          type="checkbox"
          value={watch('workEnvironment')}
          parentClassName={styles.optionsContainer}
        />

        <div className={styles.salaryRange}>
          <label className={styles.salaryLabel}>Salary Range</label>
          <div className={styles.rangeInputs}>
            <Controller
              name="minSalary"
              control={control}
              render={({ field }) => (
                <input
                  type="number"
                  placeholder="Enter min amount"
                  {...field}
                  className={`${styles.rangeInput} ${errors.minSalary ? styles.inputError : ''}`}
                />
              )}
            />
            <Controller
              name="maxSalary"
              control={control}
              render={({ field }) => (
                <input
                  type="number"
                  placeholder="Enter max amount"
                  {...field}
                  className={`${styles.rangeInput} ${errors.maxSalary ? styles.inputError : ''}`}
                />
              )}
            />
          </div>
          {(errors.minSalary || errors.maxSalary) && (
            <small className={styles.message}>
              {errors.minSalary?.message as string || errors.maxSalary?.message as string}
            </small>
          )}
        </div>
      </div>
    );
  };

  const renderCoreResponsibilitiesForm = () => {
    return (
      <div className={styles.formSection}>
        {watch().map((item: any, index: number) => (
          <div key={index} className={styles.responsibilityItem}>
            <div className={styles.formGroup}>
              <Input
                placeholder=''
                label="Title"
                type="text"
                required
                validatorMessage={errors.coreResponsibilities?.[index]?.title?.message as string ?? ""}
                name={`coreResponsibilities.${index}.title`}
                register={register}
                value={watch(`coreResponsibilities.${index}.title`)}
              />
            </div>

            <div className={styles.formGroup}>
              <Textarea
                placeholder=''
                label="Description"
                required
                validatorMessage={errors.coreResponsibilities?.[index]?.description?.message as string ?? ""}
                name={`coreResponsibilities.${index}.description`}
                register={register}
                value={watch(`coreResponsibilities.${index}.description`)}
              />
            </div>
          </div>
        ))}

        <button
          type="button"
          className={styles.addButton}
          onClick={() => {
            const current = watch('coreResponsibilities') || [];
            setValue('coreResponsibilities', [
              ...current,
              { title: '', description: '', checked: false }
            ]);
          }}
        >
          <AddIconCircle /> Add Responsibility
        </button>
      </div>
    );
  };

  const renderTechnicalSkillsForm = () => {
    return (
      <div className={styles.formSection}>
        {watch().map((item: any, index: number) => (
          <div key={index} className={styles.skillItem}>
            <div className={styles.formGroup}>
              <Input
                placeholder=''
                label="Skill Name"
                type="text"
                required
                validatorMessage={errors.technicalSkills?.[index]?.title?.message as string ?? ""}
                name={`technicalSkills.${index}.title`}
                register={register}
                value={watch(`technicalSkills.${index}.title`)}
              />
            </div>

            <div className={styles.formGroup}>
              <Textarea
                placeholder=''
                label="Expectation"
                required
                validatorMessage={errors.technicalSkills?.[index]?.expectation?.message as string ?? ""}
                name={`technicalSkills.${index}.expectation`}
                register={register}
                value={watch(`technicalSkills.${index}.expectation`)}
              />
            </div>
          </div>
        ))}

        <button
          type="button"
          className={styles.addButton}
          onClick={() => {
            const current = watch('technicalSkills') || [];
            setValue('technicalSkills', [
              ...current,
              { title: '', expectation: '' }
            ]);
          }}
        >
          <AddIconCircle /> Add Technical Skill
        </button>
      </div>
    );
  };

  const renderSoftSkillsForm = () => {
    return (
      <div className={styles.formSection}>
        {watch().map((item: any, index: number) => (
          <div key={index} className={styles.skillItem}>
            <div className={styles.formGroup}>
              <Input
                placeholder=''
                label="Skill Name"
                type="text"
                required
                validatorMessage={errors.softSkills?.[index]?.title?.message as string ?? ""}
                name={`softSkills.${index}.title`}
                register={register}
                value={watch(`softSkills.${index}.title`)}
              />
            </div>

            <div className={styles.formGroup}>
              <Textarea
                placeholder=''
                label="Description"
                required
                validatorMessage={errors.softSkills?.[index]?.description?.message as string ?? ""}
                name={`softSkills.${index}.description`}
                register={register}
                value={watch(`softSkills.${index}.description`)}
              />
            </div>

            <div className={styles.formGroup}>
              <CustomSelect
                label="Rating (1-5)"
                placeholder='Choose Rating'
                options={[1, 2, 3, 4, 5].map(num => ({
                  value: num,
                  label: num.toString()
                }))}
                onChange={(val) => setValue(`softSkills.${index}.rating`, val)}
                validatorMessage={errors.softSkills?.[index]?.rating?.message as string ?? ""}
                name={`softSkills.${index}.rating`}
                value={watch(`softSkills.${index}.rating`)}
              />
            </div>
          </div>
        ))}

        <button
          type="button"
          className={styles.addButton}
          onClick={() => {
            const current = watch('softSkills') || [];
            setValue('softSkills', [
              ...current,
              { title: '', description: '', rating: 3 }
            ]);
          }}
        >
          <AddIconCircle /> Add Soft Skill
        </button>
      </div>
    );
  };

  const renderWorkCultureTraitsForm = () => {
    return (
      <div className={styles.formSection}>
        {watch().map((item: any, index: number) => (
          <div key={index} className={styles.traitItem}>
            <div className={styles.formGroup}>
              <Input
                placeholder=''
                label="Trait Name"
                type="text"
                required
                validatorMessage={errors.workCultureTraits?.[index]?.title?.message as string ?? ""}
                name={`workCultureTraits.${index}.title`}
                register={register}
                value={watch(`workCultureTraits.${index}.title`)}
              />
            </div>

            <div className={styles.formGroup}>
              <Textarea
                placeholder=''
                label="Description"
                required
                validatorMessage={errors.workCultureTraits?.[index]?.description?.message as string ?? ""}
                name={`workCultureTraits.${index}.description`}
                register={register}
                value={watch(`workCultureTraits.${index}.description`)}
              />
            </div>
          </div>
        ))}

        <button
          type="button"
          className={styles.addButton}
          onClick={() => {
            const current = watch('workCultureTraits') || [];
            setValue('workCultureTraits', [
              ...current,
              { title: '', description: '' }
            ]);
          }}
        >
          <AddIconCircle /> Add Work Culture Trait
        </button>
      </div>
    );
  };

  const renderWorkStylesForm = () => {
    return (
      <div className={styles.formSection}>
        {watch().map((item: any, index: number) => (
          <div key={index} className={styles.workStyleItem}>
            <div className={styles.formGroup}>
              <Input
                placeholder=''
                label="Description"
                type="text"
                required
                validatorMessage={errors.workStyles?.[index]?.description?.message as string ?? ""}
                name={`workStyles.${index}.description`}
                register={register}
                value={watch(`workStyles.${index}.description`)}
              />
            </div>

            <div className={styles.formGroup}>
              <CustomSelect
                label="Rating (1-7)"
                placeholder='Choose Rating'
                options={[1, 2, 3, 4, 5, 6, 7].map(num => ({
                  value: num,
                  label: num.toString()
                }))}
                onChange={(val) => setValue(`workStyles.${index}.rating`, val)}
                validatorMessage={errors.workStyles?.[index]?.rating?.message as string ?? ""}
                name={`workStyles.${index}.rating`}
                value={watch(`workStyles.${index}.rating`)}
              />
            </div>
          </div>
        ))}

        <button
          type="button"
          className={styles.addButton}
          onClick={() => {
            const current = watch('workStyles') || [];
            setValue('workStyles', [
              ...current,
              { description: '', rating: 4 }
            ]);
          }}
        >
          <AddIconCircle /> Add Work Style
        </button>
      </div>
    );
  };

  const renderPersonalityAlignmentsForm = () => {
    return (
      <div className={styles.formSection}>
        {watch().map((item: any, index: number) => (
          <div key={index} className={styles.alignmentItem}>
            <div className={styles.formGroup}>
              <Input
                placeholder=''
                label="Category"
                type="text"
                required
                validatorMessage={errors.personalityAlignments?.[index]?.category?.message as string ?? ""}
                name={`personalityAlignments.${index}.category`}
                register={register}
                value={watch(`personalityAlignments.${index}.category`)}
              />
            </div>

            <div className={styles.formGroup}>
              <Textarea
                placeholder=''
                label="Description"
                required
                validatorMessage={errors.personalityAlignments?.[index]?.description?.message as string ?? ""}
                name={`personalityAlignments.${index}.description`}
                register={register}
                value={watch(`personalityAlignments.${index}.description`)}
              />
            </div>

            <div className={styles.formGroup}>
              <Input
                placeholder=''
                label="Style"
                type="text"
                required
                validatorMessage={errors.personalityAlignments?.[index]?.style?.message as string ?? ""}
                name={`personalityAlignments.${index}.style`}
                register={register}
                value={watch(`personalityAlignments.${index}.style`)}
              />
            </div>

            <div className={styles.formGroup}>
              <Textarea
                placeholder=''
                label="Implication"
                required
                validatorMessage={errors.personalityAlignments?.[index]?.implication?.message as string ?? ""}
                name={`personalityAlignments.${index}.implication`}
                register={register}
                value={watch(`personalityAlignments.${index}.implication`)}
              />
            </div>
          </div>
        ))}

        <button
          type="button"
          className={styles.addButton}
          onClick={() => {
            const current = watch('personalityAlignments') || [];
            setValue('personalityAlignments', [
              ...current,
              { category: '', description: '', style: '', implication: '' }
            ]);
          }}
        >
          <AddIconCircle /> Add Personality Alignment
        </button>
      </div>
    );
  };

  const renderFormContent = () => {
    switch (section) {
      case 'basicExpectation':
        return renderBasicExpectationForm();
      case 'coreResponsibilities':
        return renderCoreResponsibilitiesForm();
      case 'technicalSkills':
        return renderTechnicalSkillsForm();
      case 'softSkills':
        return renderSoftSkillsForm();
      case 'workCultureTraits':
        return renderWorkCultureTraitsForm();
      case 'workStyles':
        return renderWorkStylesForm();
      case 'personalityAlignments':
        return renderPersonalityAlignmentsForm();
      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h2>Edit {sectionTitle}</h2>
          <button onClick={onClose} className={styles.closeButton}>
            <CloseCircleGreyIcon />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className={styles.formContainer}>

          <div className={styles.formContent}>
            {renderFormContent()}
          </div>


          <div className={styles.modalFooter}>
            <Button
              className={styles.cancelButton}
              type="outline"
              onClick={onClose}
            >
              Cancel
            </Button>
            <button type="submit" className={styles.saveButton}>
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditModal;